import type { FC } from 'react';
import Image from '../../../../shared/components/Image';
import { useTranslator } from '../../../../shared/hooks/useTranslations';
import { testIds } from '../../../../core/testIds';
import { JACKPOT_STATISTICS_SORT } from '../../constants';
import { formatJackpotLevelLabel, normalizeJackpotLevelName } from '../../utils/jackpotStatistics';
import type { IJackpotWinLevel, JackpotStatisticsSortMode } from '../../types/jackpotStatistics.types';
import JackpotLevelLabel from './JackpotLevelLabel';
import styles from './JackpotStatistics.module.css';

interface JackpotStatisticsFooterProps {
  jackpotName: string;
  levels: IJackpotWinLevel[];
  selectedLevelName: string | null;
  /** Clicking a tab selects it; clicking the selected tab clears the filter. */
  onToggleLevel: (levelName: string) => void;
  sortMode: JackpotStatisticsSortMode;
  onSelectSort: (sortMode: JackpotStatisticsSortMode) => void;
  onClose: () => void;
}

/**
 * A mouse click must not move focus off the win list onto the clicked button: focus is what the
 * keyboard scrolls, so a click would otherwise leave Space / the arrow keys scrolling nothing.
 * Keyboard focus (Tab) is unaffected.
 */
const keepFocusOnList = (e: React.MouseEvent): void => e.preventDefault();

/** Bottom panel: the filters and the close button. */
const JackpotStatisticsFooter: FC<JackpotStatisticsFooterProps> = ({
  jackpotName,
  levels,
  selectedLevelName,
  onToggleLevel,
  sortMode,
  onSelectSort,
  onClose,
}) => {
  const translate = useTranslator();
  // Every level gets a chip, a never-won one included (it shows "0" and selects the empty state);
  // the row is only worth showing when there is an actual choice to make.
  const showLevelChips = levels.length > 1;

  return (
    <div className={styles.footerPanel}>
      {showLevelChips && (
        <div className={styles.levelChips}>
          {levels.map((level) => {
            const levelName = normalizeJackpotLevelName(level.levelName);
            const isSelected = selectedLevelName === levelName;
            return (
              <button
                key={levelName}
                type="button"
                className={styles.levelChip}
                data-level={levelName}
                data-selected={isSelected}
                aria-pressed={isSelected}
                aria-label={formatJackpotLevelLabel(level.levelName)}
                data-testid={testIds.jackpots.statisticsLevelFilter(levelName, isSelected ? 'on' : 'off')}
                onMouseDown={keepFocusOnList}
                onClick={() => onToggleLevel(levelName)}
              >
                <span className={styles.levelChipLabel}>
                  <JackpotLevelLabel jackpotName={jackpotName} levelName={level.levelName} />
                </span>
                <span className={styles.levelChipCount}>{level.totalWins}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.sortRow}>
        {/* Both ordering buttons share one container: it carries the sliding selection pill,
            which is why the selected state is drawn by the container rather than the button. */}
        <div className={styles.sortGroup} data-selected={sortMode}>
          <button
            type="button"
            className={styles.sortButton}
            data-selected={sortMode === JACKPOT_STATISTICS_SORT.LAST_100}
            aria-pressed={sortMode === JACKPOT_STATISTICS_SORT.LAST_100}
            data-testid={testIds.jackpots.statisticsSortFilter(
              JACKPOT_STATISTICS_SORT.LAST_100,
              sortMode === JACKPOT_STATISTICS_SORT.LAST_100 ? 'on' : 'off',
            )}
            onMouseDown={keepFocusOnList}
            onClick={() => onSelectSort(JACKPOT_STATISTICS_SORT.LAST_100)}
          >
            {translate('statistics.last_100', 'Last 100')}
          </button>
          <button
            type="button"
            className={styles.sortButton}
            data-selected={sortMode === JACKPOT_STATISTICS_SORT.TOP_10}
            aria-pressed={sortMode === JACKPOT_STATISTICS_SORT.TOP_10}
            data-testid={testIds.jackpots.statisticsSortFilter(
              JACKPOT_STATISTICS_SORT.TOP_10,
              sortMode === JACKPOT_STATISTICS_SORT.TOP_10 ? 'on' : 'off',
            )}
            onMouseDown={keepFocusOnList}
            onClick={() => onSelectSort(JACKPOT_STATISTICS_SORT.TOP_10)}
          >
            {translate('statistics.top_10', 'Top 10')}
          </button>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          aria-label={translate('statistics.close', 'Close')}
          data-testid={testIds.jackpots.statisticsCloseButton}
          onMouseDown={keepFocusOnList}
          onClick={onClose}
        >
          <Image className={styles.closeIcon} src="/icons/statistics_close_icon" manualAffix=".svg" alt="" />
        </button>
      </div>
    </div>
  );
};

export default JackpotStatisticsFooter;
